import axiosInstance from '../utils/axios';
import { getRole } from '../utils/role';

const DASHBOARD_ENDPOINTS = {
  SUPPORT: '/super-admin/dashboard-metrics',
  PARTNER: '/admin/dashboard-metrics',
  FIELD_OFFICER: '/field-officer/dashboard-metrics',
  FARMER: '/user/dashboard-metrics',
  TEAM_MEMBER: '/admin/dashboard-metrics',
};

/**
 * Fetch dashboard KPI metrics for the current logged-in user.
 * @param {{ from?: string, to?: string }} params  Optional global date override (ISO strings).
 * @returns {Promise<{ success: boolean, role: string, metrics: Array }>}
 */
export const fetchDashboardMetrics = async (params = {}) => {
  const role = getRole();
  const endpoint = DASHBOARD_ENDPOINTS[role];

  if (!endpoint) throw new Error(`Unknown role: ${role}`);

  const queryParams = {};
  if (params.from) queryParams.from = params.from;
  if (params.to) queryParams.to = params.to;
  if (params.state) queryParams.state = params.state;
  if (params.city) queryParams.city = params.city;
  if (params.crop) queryParams.crop = params.crop;

  const { data } = await axiosInstance.get(endpoint, { params: queryParams });
  return data;
};

export const fetchDashboardFilters = async () => {
  const role = getRole();
  if (role !== 'PARTNER' && role !== 'TEAM_MEMBER') return { success: false, states: [], cities: [], crops: [] };
  
  const { data } = await axiosInstance.get('/admin/dashboard-filters');
  return data;
};

export const fetchCsrStateCoverage = async (adminId) => {
  const endpoint = getRole() === 'SUPPORT' && adminId
    ? `/super-admin/csr/state-coverage/${adminId}`
    : '/admin/csr/state-coverage';
  const { data } = await axiosInstance.get(endpoint);
  return data;
};

export const fetchPartnerDashboardForAdmin = async (adminId, params = {}) => {
  const queryParams = { ...params };
  const { data } = await axiosInstance.get(`/super-admin/admin-dashboard-metrics/${adminId}`, { params: queryParams });
  return data;
};

export const fetchPartnerFiltersForAdmin = async (adminId) => {
  const { data } = await axiosInstance.get(`/super-admin/admin-dashboard-filters/${adminId}`);
  return data;
};

// Support reads a chosen partner via /super-admin/.../:adminId; partners and team members
// are scoped by their login via /admin/...
const csrUrl = (resource, adminId, suffix = '') => (getRole() === 'SUPPORT' && adminId
  ? `/super-admin/csr/${resource}/${encodeURIComponent(adminId)}${suffix}`
  : `/admin/csr/${resource}${suffix}`);

const cleanParams = (params = {}) => Object.fromEntries(
  Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
);

export const fetchCsrSoilTests = async (adminId, params) => {
  const { data } = await axiosInstance.get(csrUrl('soil-tests', adminId), { params: cleanParams(params) });
  return data;
};

export const fetchCsrSoilTest = async (adminId, sampleId) => {
  const { data } = await axiosInstance.get(csrUrl('soil-tests', adminId, `/${encodeURIComponent(sampleId)}`));
  return data;
};

export const fetchCsrFarmers = async (adminId, params) => {
  const { data } = await axiosInstance.get(csrUrl('farmers', adminId), { params: cleanParams(params) });
  return data;
};

export const fetchCsrSoilDidis = async (adminId, params) => {
  const { data } = await axiosInstance.get(csrUrl('soil-didis', adminId), { params: cleanParams(params) });
  return data;
};
